import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { H1Title, Paragraph } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import SubmissionService from "../services/submissionService";
import CompetitionService from "../services/competitionService";
import { Success, Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSave, faArrowLeft, faArrowRight, faPaperPlane, faFileContract, faTrash, faRocket } from "@fortawesome/free-solid-svg-icons";
import SignaturePad from "../components/signature/SignaturePad";
import hdfLogo from "../assets/images/logo/logo.svg";

const MIN_CHARS = {
  problemStatement: 100,
  proposedIntervention: 100,
  targetPopulation: 50,
  implementationPlan: 100,
  'anticipatedOutcome.shortTerm': 50,
  'anticipatedOutcome.longTerm': 50,
  'anticipatedOutcome.problemAlignment': 50,
};

const STEPS = [
  { key: 'problemStatement', title: 'Problem Statement', desc: 'What specific issue are you addressing? Use data and context to demonstrate your understanding.', minChars: 100 },
  { key: 'proposedIntervention', title: 'Proposed Intervention', desc: 'What exactly will you do? Describe your solution clearly and why this approach is appropriate.', minChars: 100 },
  { key: 'targetPopulation', title: 'Target Population', desc: 'Who will this project serve? Be specific about size, demographics, and circumstances.', minChars: 50 },
  { key: 'implementationPlan', title: 'Implementation Plan & Timeline', desc: 'Provide a step-by-step plan with key milestones and a realistic timeline.', minChars: 100 },
  { key: 'budget', title: 'Budget', desc: 'Provide an itemized budget. Each line item should include a cost estimate and justification.' },
  { key: 'anticipatedOutcome', title: 'Anticipated Outcome', desc: 'What do you expect this project to accomplish? Address short-term, long-term, and problem alignment.' },
  { key: 'supportingDocs', title: 'Supporting Documents', desc: 'Optionally upload any supporting documents for your proposal (not required).' },
  { key: 'agreement', title: 'Review & Sign', desc: 'Review the participation agreement and sign to submit your proposal.' },
];

export default function Submission() {
  const [step, setStep] = useState(-1);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Form state for each section
  const [form, setForm] = useState({
    problemStatement: '',
    proposedIntervention: '',
    targetPopulation: '',
    implementationPlan: '',
    budget: { totalAmount: 0, lineItems: [{ description: '', amount: '', justification: '' }] },
    anticipatedOutcome: { shortTerm: '', longTerm: '', problemAlignment: '' },
    supportingDocsUrl: '',
  });

  useEffect(() => {
    loadSubmission();
  }, []);

  const loadSubmission = async () => {
    try {
      const { data: compData } = await CompetitionService.getActive();
      const competitionId = compData.data.competition._id;

      const { data } = await SubmissionService.createOrGetDraft({ competitionId });
      const sub = data.data.submission;
      setSubmission(sub);

      if (sub.proposal) {
        setForm({
          problemStatement: sub.proposal.problemStatement || '',
          proposedIntervention: sub.proposal.proposedIntervention || '',
          targetPopulation: sub.proposal.targetPopulation || '',
          implementationPlan: sub.proposal.implementationPlan || '',
          budget: sub.proposal.budget?.lineItems?.length
            ? sub.proposal.budget
            : { totalAmount: 0, lineItems: [{ description: '', amount: '', justification: '' }] },
          anticipatedOutcome: sub.proposal.anticipatedOutcome || { shortTerm: '', longTerm: '', problemAlignment: '' },
          supportingDocsUrl: sub.proposal.supportingDocsUrl || '',
        });
        // Skip intro if the participant has already started filling anything out
        const hasContent = sub.proposal.problemStatement || sub.proposal.proposedIntervention ||
          sub.proposal.targetPopulation || sub.proposal.implementationPlan ||
          sub.proposal.budget?.lineItems?.some(i => i.description || i.amount);
        if (hasContent) setStep(0);
      }
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load submission');
    } finally { setLoading(false); }
  };

  const saveDraft = async () => {
    if (!submission) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Recalculate total
      if (payload.budget?.lineItems) {
        payload.budget.totalAmount = payload.budget.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      }
      await SubmissionService.saveDraft(submission._id, payload);
      Success('Draft saved');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSubmit = async (signatureData) => {
    setSubmitting(true);
    try {
      // Save draft first
      const payload = { ...form };
      if (payload.budget?.lineItems) {
        payload.budget.totalAmount = payload.budget.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      }
      await SubmissionService.saveDraft(submission._id, payload);
      await SubmissionService.submit(submission._id, { signatureData });
      Success('Proposal submitted successfully!');
      navigate('/dashboard');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  // Budget helpers
  const addLineItem = () => {
    setForm(prev => ({
      ...prev,
      budget: {
        ...prev.budget,
        lineItems: [...prev.budget.lineItems, { description: '', amount: '', justification: '' }]
      }
    }));
  };

  const updateLineItem = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.budget.lineItems];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, budget: { ...prev.budget, lineItems: items } };
    });
  };

  const removeLineItem = (index) => {
    if (form.budget.lineItems.length <= 1) return;
    setForm(prev => ({
      ...prev,
      budget: { ...prev.budget, lineItems: prev.budget.lineItems.filter((_, i) => i !== index) }
    }));
  };

  // Validate all sections before allowing submit
  const getValidationErrors = () => {
    const errors = [];
    if ((form.problemStatement || '').length < 100) errors.push(`Problem Statement (${form.problemStatement.length}/100 chars)`);
    if ((form.proposedIntervention || '').length < 100) errors.push(`Proposed Intervention (${form.proposedIntervention.length}/100 chars)`);
    if ((form.targetPopulation || '').length < 50) errors.push(`Target Population (${form.targetPopulation.length}/50 chars)`);
    if ((form.implementationPlan || '').length < 100) errors.push(`Implementation Plan (${form.implementationPlan.length}/100 chars)`);
    if (!form.budget?.lineItems?.some(i => i.description && i.amount)) errors.push('Budget (at least 1 complete line item)');
    if ((form.anticipatedOutcome?.shortTerm || '').length < 50) errors.push(`Short-term Outcome (${(form.anticipatedOutcome?.shortTerm || '').length}/50 chars)`);
    if ((form.anticipatedOutcome?.longTerm || '').length < 50) errors.push(`Long-term Outcome (${(form.anticipatedOutcome?.longTerm || '').length}/50 chars)`);
    if ((form.anticipatedOutcome?.problemAlignment || '').length < 50) errors.push(`Problem Alignment (${(form.anticipatedOutcome?.problemAlignment || '').length}/50 chars)`);
    return errors;
  };

  if (loading) return <div className="flex justify-center py-20 text-grey-667">Loading...</div>;
  if (submission?.status === 'submitted') {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="w-16 h-16 bg-green-light rounded-full flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon icon={faCheck} className="text-green text-2xl" />
        </div>
        <H1Title size="sm">Proposal Submitted</H1Title>
        <Paragraph className="text-grey-667 mt-2">Your proposal has been submitted and is under review.</Paragraph>
        <AppButton onClick={() => navigate('/dashboard')} fullWidth={false} className="mt-6 px-8">Back to Dashboard</AppButton>
      </div>
    );
  }

  if (step === -1) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faRocket} className="text-primary text-2xl" />
          </div>
          <H1Title size="sm">Before you begin</H1Title>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-6 space-y-4">
          <Paragraph size="base" className="text-grey-dark">
            Please review the <strong>Competition Description Packet</strong>, <strong>Location Brief</strong>, and <strong>Judging Rubric</strong> attached on the dashboard before starting your proposal.
          </Paragraph>
          <Paragraph size="base" className="text-grey-dark">
            You have until <strong>May 31</strong> to submit your proposal.
          </Paragraph>
          <Paragraph size="base" className="text-grey-dark">
            For any questions, reach out to <a href="mailto:iman@hdfund.org" className="text-primary hover:underline">iman@hdfund.org</a>.
          </Paragraph>
          <Paragraph size="base" className="text-grey-dark">
            We're excited to see what you build!
          </Paragraph>
        </div>
        <div className="flex flex-col items-center gap-3">
          <AppButton onClick={() => setStep(0)} fullWidth={false} className="px-10">
            Start Application <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </AppButton>
          <button onClick={() => navigate('/dashboard')} className="text-sm text-grey-667 hover:text-primary cursor-pointer">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentStep = STEPS[step];
  const budgetTotal = form.budget.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <H1Title size="xs">Proposal Submission</H1Title>
          <span className="text-sm text-grey-667">Step {step + 1} of {STEPS.length}</span>
        </div>
        <div className="flex gap-1">
          {STEPS.map((s, i) => (
            <button key={i} onClick={() => setStep(i)} className={`flex-1 h-2 rounded-full cursor-pointer transition-colors ${
              i <= step ? 'bg-primary' : 'bg-grey-200'
            }`} />
          ))}
        </div>
        <div className="flex gap-1 mt-2">
          {STEPS.map((s, i) => (
            <button key={i} onClick={() => setStep(i)} className={`flex-1 text-[10px] cursor-pointer ${
              i === step ? 'text-primary font-semibold' : 'text-grey-98'
            }`}>
              {s.title}
            </button>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-grey-200 p-6">
        <h2 className="text-lg font-bold mb-1">{currentStep.title}</h2>
        <p className="text-sm text-grey-667 mb-6">{currentStep.desc}</p>

        {step < 4 ? (
          /* Text sections: steps 0-3 */
          <>
            <textarea
              className={`w-full border rounded-lg p-4 text-sm min-h-[300px] outline-none focus:border-primary resize-y ${
                currentStep.minChars && (form[currentStep.key] || '').length >= currentStep.minChars
                  ? 'border-green'
                  : 'border-grey-200'
              }`}
              placeholder={`Write your ${currentStep.title.toLowerCase()} here...`}
              value={form[currentStep.key]}
              onChange={(e) => setForm({ ...form, [currentStep.key]: e.target.value })}
            />
            {currentStep.minChars && (
              <div className={`text-xs mt-2 flex justify-between ${
                (form[currentStep.key] || '').length >= currentStep.minChars ? 'text-green' : 'text-grey-98'
              }`}>
                <span>{(form[currentStep.key] || '').length >= currentStep.minChars ? 'Minimum met' : `Minimum ${currentStep.minChars} characters required`}</span>
                <span className="font-mono">{(form[currentStep.key] || '').length} / {currentStep.minChars}</span>
              </div>
            )}
          </>
        ) : step === 6 ? (
          /* Supporting Documents step (optional) */
          <div className="space-y-4">
            <p className="text-sm text-grey-667">If you have any supporting documents (research, diagrams, letters of support, etc.), paste a link below. This is <strong>not required</strong> to submit your proposal.</p>
            <div>
              <label className="block text-sm font-semibold mb-2">Document Link (Google Drive, Dropbox, etc.)</label>
              <input
                type="url"
                className="w-full border border-grey-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-primary"
                placeholder="https://drive.google.com/..."
                value={form.supportingDocsUrl}
                onChange={(e) => setForm({ ...form, supportingDocsUrl: e.target.value })}
              />
            </div>
            <p className="text-xs text-grey-98">Make sure the link is publicly accessible or shared with the right permissions.</p>
          </div>
        ) : step === 7 ? (
          /* Agreement & Signature step */
          <div className="space-y-6">
            <div className="bg-grey-bg rounded-lg p-5 max-h-[420px] overflow-y-auto text-sm text-grey-dark space-y-4 border border-grey-200">
              <div className="text-center pb-3 border-b border-grey-200">
                <img src={hdfLogo} alt="HDF" className="h-16 mx-auto mb-2 object-contain" />
                <p className="font-bold text-black uppercase tracking-wide">Human Development Fund</p>
                <p className="italic text-xs text-grey-667">The Human Development Lab Competition</p>
                <p className="font-bold text-black mt-2">Participation Agreement</p>
                <p className="text-xs text-grey-98">To be executed at the time of submission</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">1. Parties</p>
                <p>This Participation Agreement (the "Agreement") is entered into by and between Human Development Fund, a Michigan nonprofit corporation with its principal office at 26877 Northwestern Hwy, Southfield, Michigan ("HDF"), and the undersigned participant ("Participant"). Where Participant is part of a team, each team member must execute a separate copy of this Agreement, and the team must designate one member as the Team Lead as indicated below.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">2. Program</p>
                <p>HDF is hosting the Human Development Lab Competition (the "Competition"), a program inviting proposals from eligible participants for community-impact projects to be implemented in partnership with HDF's in-country affiliates. For the pilot year, implementation will take place in Kenya in collaboration with HDF Kenya. The full competition structure, timeline, and judging criteria are set forth in the Competition Overview provided to Participant and incorporated herein by reference.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">3. Eligibility and Participant Representations</p>
                <p>Participant represents and warrants that, as of the date of signature:</p>
                <ul className="list-disc list-inside space-y-1 mt-1 ml-2">
                  <li>Participant is at least eighteen (18) years of age.</li>
                  <li>Participant is currently enrolled as an undergraduate or graduate student, or is an early professional within zero (0) to three (3) years of graduation.</li>
                  <li>If participating as part of a team, the team consists of no more than three (3) members in total.</li>
                  <li>Participant is submitting only one (1) entry to the Competition, whether individually or as part of a single team.</li>
                  <li>All information submitted to HDF is truthful, accurate, and complete to the best of Participant's knowledge.</li>
                </ul>
              </div>

              <div>
                <p className="font-bold text-black mb-1">4. Intellectual Property Assignment</p>
                <p><strong>4.1 Assignment.</strong> Participant irrevocably assigns, transfers, and conveys to HDF all right, title, and interest, worldwide and in perpetuity, in and to any and all ideas, concepts, proposals, written materials, diagrams, designs, data, presentations, audiovisual materials, code, and any other work product submitted to HDF in connection with the Competition (collectively, the "Submission"), including all associated copyrights, patent rights, trademark rights, trade secrets, and any other intellectual property rights recognized under applicable law.</p>
                <p className="mt-1"><strong>4.2 HDF's Rights.</strong> HDF shall have the unrestricted right to use, reproduce, distribute, adapt, modify, expand, implement, publicly display, publicly perform, sublicense, and create derivative works from the Submission, in whole or in part, without further consent from or compensation to Participant.</p>
                <p className="mt-1"><strong>4.3 Waiver of Moral Rights.</strong> To the maximum extent permitted by law, Participant waives any moral rights or similar rights in the Submission and agrees not to assert any such rights against HDF or its licensees.</p>
                <p className="mt-1"><strong>4.4 Further Assurances.</strong> Participant agrees to execute any further documents and take any further actions reasonably requested by HDF to perfect or record the assignment set forth in this Section 4.</p>
                <p className="mt-1"><strong>4.5 Originality.</strong> Participant represents and warrants that the Submission is Participant's original work; does not infringe upon the intellectual property, privacy, or publicity rights of any third party; and is not subject to any prior assignment, license, or obligation that would conflict with the assignment in this Section 4.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">5. Volunteer Status; No Compensation</p>
                <p>Participant acknowledges and agrees that participation in the Competition, at every stage including submission, finalist selection, fundraising, implementation, and any site visit, is undertaken on a strictly volunteer and unpaid basis. Participant is not an employee, agent, contractor, or joint venturer of HDF, and no compensation, wages, salary, benefits, stipend, royalty, or other payment of any kind shall be owed to Participant at any stage of the Competition.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">6. Confidentiality</p>
                <p><strong>6.1 HDF Confidentiality Obligations.</strong> Submissions will be reviewed internally by HDF staff, judges, and advisors on a need-to-know basis only. HDF will not publicly disclose the contents of any non-winning Submission without the Participant's prior written consent, except as reasonably required to operate the Competition, evaluate the Submission, fulfill HDF's reporting obligations to donors, regulators, or partners, or to publicize or implement winning or selected Submissions.</p>
                <p className="mt-1"><strong>6.2 Participant Confidentiality Obligations.</strong> Participant agrees to hold in confidence any non-public information shared by HDF in connection with the Competition, including but not limited to HDF program information, partner information, judging deliberations, and information about other participants or Submissions.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">7. Background Check Authorization</p>
                <p>If Participant advances to the finalist stage, Participant authorizes HDF, or a third-party service provider engaged by HDF, to conduct a background check on Participant. Such background check may include verification of identity, education, employment, and review of criminal history records, in each case to the extent permitted by applicable law. Participant will be asked to sign a separate consent form at the finalist stage consistent with the Fair Credit Reporting Act and any applicable state equivalents. HDF reserves the right to disqualify any Participant based on the results of the background check, in HDF's sole discretion, or who declines to consent to one.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">8. Publicity and Use of Name and Likeness</p>
                <p>Participant grants HDF a perpetual, royalty-free, worldwide right to use Participant's name, likeness, photograph, voice, biographical information, and statements made in connection with the Competition in HDF's marketing, fundraising, educational, and reporting materials, in any media now known or later developed. HDF shall use reasonable efforts to present Participant in a respectful and accurate manner.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">9. Code of Conduct</p>
                <p>Participant agrees to comply with HDF's Code of Conduct and any other policies provided to Participant during the Competition, including policies regarding anti-harassment, non-discrimination, anti-corruption, safeguarding, and data protection. HDF may disqualify any Participant for material violation of these policies.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">10. Disqualification; Modification; Cancellation</p>
                <p>HDF may, in its sole discretion, disqualify any Participant or Submission that does not comply with this Agreement or the Competition rules; modify the Competition structure, timeline, or judging criteria at any time with notice to Participant; or suspend or cancel the Competition in whole or in part. HDF shall have no liability to Participant in connection with any such action.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">11. No Warranty; Release</p>
                <p>The Competition is offered on an "as is" basis. Participant releases, waives, and discharges HDF, its directors, officers, employees, volunteers, affiliates, and agents (collectively, the "HDF Parties") from any and all claims, demands, or causes of action arising out of or relating to Participant's Submission, evaluation, or participation in the Competition, except to the extent caused by the gross negligence or willful misconduct of the HDF Parties.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">12. Governing Law and Venue</p>
                <p>This Agreement shall be governed by and construed under the laws of the State of Michigan, without regard to its conflict of laws principles. Any dispute arising out of or relating to this Agreement shall be brought exclusively in the state or federal courts located in Oakland County, Michigan, and the parties consent to the personal jurisdiction of such courts.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">13. Entire Agreement; Amendments</p>
                <p>This Agreement, together with the Competition Overview and any policies incorporated by reference, constitutes the entire agreement between the parties with respect to the subject matter hereof. This Agreement may be amended only by a written instrument signed by both parties. If Participant advances to winner selection, Participant will be required to execute an additional Implementation Agreement, and if Participant travels in connection with the Competition, an additional Travel Waiver.</p>
              </div>

              <div>
                <p className="font-bold text-black mb-1">14. Signature</p>
                <p>By signing below, Participant acknowledges that Participant has read, understood, and agreed to be bound by this Agreement.</p>
              </div>
            </div>

            {submitting ? (
              <div className="text-center py-8 text-grey-667">Submitting your proposal...</div>
            ) : (
              <SignaturePad
                onSave={handleSubmit}
                onCancel={() => setStep(step - 1)}
              />
            )}
          </div>
        ) : step === 4 ? (
          /* Budget step */
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold">Total: <span className="text-primary">${budgetTotal.toLocaleString()}</span></p>
              <AppButton onClick={addLineItem} size="sm" variant="outline" fullWidth={false} className="px-4">
                + Add Item
              </AppButton>
            </div>
            {form.budget.lineItems.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-start border border-grey-200 rounded-lg p-4">
                <div className="col-span-5">
                  <label className="block text-xs text-grey-667 mb-1">Description</label>
                  <input className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none" placeholder="e.g. Water filter units" value={item.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-grey-667 mb-1">Amount ($)</label>
                  <input type="number" className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none" placeholder="0" value={item.amount} onChange={(e) => updateLineItem(i, 'amount', e.target.value)} />
                </div>
                <div className="col-span-4">
                  <label className="block text-xs text-grey-667 mb-1">Justification</label>
                  <input className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none" placeholder="Why this cost?" value={item.justification} onChange={(e) => updateLineItem(i, 'justification', e.target.value)} />
                </div>
                <div className="col-span-1 flex items-end pb-1">
                  {form.budget.lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(i)} className="text-red-500 text-sm cursor-pointer hover:text-red-600 py-2"><FontAwesomeIcon icon={faTrash} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Anticipated Outcome step */
          <div className="space-y-4">
            {[
              { key: 'shortTerm', label: 'Short-term Outcomes', placeholder: 'What changes during or immediately after implementation?', min: 50 },
              { key: 'longTerm', label: 'Long-term Outcomes', placeholder: 'What does sustained impact look like 6-12 months after completion?', min: 50 },
              { key: 'problemAlignment', label: 'Problem Alignment', placeholder: 'How directly does this intervention address the original problem?', min: 50 },
            ].map((field) => {
              const val = form.anticipatedOutcome?.[field.key] || '';
              const met = val.length >= field.min;
              return (
                <div key={field.key}>
                  <label className="block text-sm font-semibold mb-2">{field.label}</label>
                  <textarea
                    className={`w-full border rounded-lg p-4 text-sm min-h-[120px] outline-none focus:border-primary resize-y ${met ? 'border-green' : 'border-grey-200'}`}
                    placeholder={field.placeholder}
                    value={val}
                    onChange={(e) => setForm({ ...form, anticipatedOutcome: { ...form.anticipatedOutcome, [field.key]: e.target.value } })}
                  />
                  <div className={`text-xs mt-1 flex justify-between ${met ? 'text-green' : 'text-grey-98'}`}>
                    <span>{met ? 'Minimum met' : `Minimum ${field.min} characters required`}</span>
                    <span className="font-mono">{val.length} / {field.min}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Navigation — hidden on agreement step (SignaturePad has its own controls) */}
      {step !== 7 && (
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            {step > 0 && (
              <AppButton onClick={() => setStep(step - 1)} variant="outline" fullWidth={false} className="px-6">
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Previous
              </AppButton>
            )}
            <AppButton onClick={saveDraft} variant="outline" fullWidth={false} className="px-6" loading={saving}>
              <FontAwesomeIcon icon={faSave} className="mr-2" /> Save Draft
            </AppButton>
          </div>

          <AppButton onClick={() => {
            saveDraft();
            if (step === 6) {
              const errors = getValidationErrors();
              if (errors.length > 0) {
                ToastError(`Please complete: ${errors.join(', ')}`);
                return;
              }
            }
            setStep(step + 1);
          }} fullWidth={false} className="px-6">
            {step === 6 ? (
              <><FontAwesomeIcon icon={faFileContract} className="mr-2" /> Review & Sign</>
            ) : (
              <>Next <FontAwesomeIcon icon={faArrowRight} className="ml-2" /></>
            )}
          </AppButton>
        </div>
      )}
    </div>
  );
}
